import React, { useState, useEffect } from 'react'
import { Settings, Save, RefreshCw, Percent, Calendar, DollarSign } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../../services/api'

// HR-related setting keys to filter and display
const HR_SETTING_KEYS = [
  'EPF_EMPLOYEE_CONTRIBUTION',
  'EPF_EMPLOYER_CONTRIBUTION', 
  'ETF_EMPLOYER_CONTRIBUTION',
  'EXTRA_LEAVES_DEDUCTION_PER_DAY',
  'HR_TAX_RATE',
  'SALARY_GENERATION_DAY'
]

const HRMSettings = () => {
  const [settings, setSettings] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editedValues, setEditedValues] = useState({})

  // Handle input changes
  const handleValueChange = (key, value) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: value
    }))
  }

  // Save a specific setting
  const saveSetting = async (key, value) => {
    try {
      setSaving(true)
      await api.patch(`/system-configs/${key}`, {
        value: parseFloat(value) || value
      })
      
      toast.success('Setting updated successfully')
      
      // Update local state
      setSettings(prev => prev.map(setting => 
        setting.key === key ? { ...setting, value: parseFloat(value) || value } : setting
      ))
      
      // Clear edited value
      setEditedValues(prev => {
        const newValues = { ...prev }
        delete newValues[key]
        return newValues
      })
      
    } catch (error) {
      console.error('Error saving setting:', error)
      toast.error(error.response?.data?.message || 'Failed to update setting')
    } finally {
      setSaving(false)
    }
  }

  // Get setting icon based on key
  const getSettingIcon = (key) => {
    if (key.includes('EPF') || key.includes('ETF') || key.includes('TAX')) {
      return <Percent className="h-5 w-5 text-blue-600" />
    } else if (key.includes('SALARY_GENERATION')) {
      return <Calendar className="h-5 w-5 text-green-600" />
    } else if (key.includes('DEDUCTION')) {
      return <DollarSign className="h-5 w-5 text-red-600" />
    }
    return <Settings className="h-5 w-5 text-gray-600" />
  }

  // Get current display value
  const getCurrentValue = (setting) => {
    return editedValues[setting.key] !== undefined 
      ? editedValues[setting.key] 
      : setting.value
  }

  // Check if value has been edited
  const hasChanges = (setting) => {
    return editedValues[setting.key] !== undefined && 
           editedValues[setting.key] !== setting.value
  }

  // Refresh settings function
  const refreshSettings = async () => {
    toast.loading('Refreshing settings...', { id: 'refresh' })
    try {
      setLoading(true)
      const response = await api.get('/system-configs')
      
      console.log('Raw API response:', response)
      
      // Handle different response structures
      let allSettings = []
      if (Array.isArray(response)) {
        allSettings = response
      } else if (Array.isArray(response.data)) {
        allSettings = response.data
      } else if (response.data?.configs && Array.isArray(response.data.configs)) {
        allSettings = response.data.configs
      } else if (response.configs && Array.isArray(response.configs)) {
        allSettings = response.configs
      } else if (response.data) {
        // If response.data is an object, try to find arrays within it
        const possibleArrays = Object.values(response.data).filter(Array.isArray)
        if (possibleArrays.length > 0) {
          allSettings = possibleArrays[0]
        }
      }
      
      // Ensure allSettings is an array before filtering
      if (!Array.isArray(allSettings)) {
        console.error('Unable to extract array from response:', response)
        toast.error('Invalid response format from server', { id: 'refresh' })
        return
      }
      
      // Filter to only show HR-related settings
      const hrSettings = allSettings.filter(setting => 
        setting && setting.key && HR_SETTING_KEYS.includes(setting.key)
      )
      
      setSettings(hrSettings)
      setEditedValues({}) // Clear any pending changes
      toast.success('Settings refreshed successfully!', { id: 'refresh' })
      console.log('Refreshed HR settings:', hrSettings)
    } catch (error) {
      console.error('Error refreshing settings:', error)
      toast.error('Failed to refresh settings', { id: 'refresh' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Load system configurations
    const loadSettings = async () => {
      try {
        setLoading(true)
        const response = await api.get('/system-configs')
        
        console.log('Raw API response:', response)
        
        // Handle different response structures
        let allSettings = []
        if (Array.isArray(response)) {
          allSettings = response
        } else if (Array.isArray(response.data)) {
          allSettings = response.data
        } else if (response.data?.configs && Array.isArray(response.data.configs)) {
          allSettings = response.data.configs
        } else if (response.configs && Array.isArray(response.configs)) {
          allSettings = response.configs
        } else if (response.data) {
          // If response.data is an object, try to find arrays within it
          const possibleArrays = Object.values(response.data).filter(Array.isArray)
          if (possibleArrays.length > 0) {
            allSettings = possibleArrays[0]
          }
        }
        
        console.log('Processed allSettings:', allSettings)
        console.log('Is array?', Array.isArray(allSettings))
        
        // Ensure allSettings is an array before filtering
        if (!Array.isArray(allSettings)) {
          console.error('Unable to extract array from response:', response)
          toast.error('Invalid response format from server')
          setSettings([])
          return
        }
        
        // Filter to only show HR-related settings
        const hrSettings = allSettings.filter(setting => 
          setting && setting.key && HR_SETTING_KEYS.includes(setting.key)
        )
        
        setSettings(hrSettings)
        console.log('Loaded HR settings:', hrSettings)
      } catch (error) {
        console.error('Error loading settings:', error)
        toast.error('Failed to load HRM settings')
        setSettings([])
      } finally {
        setLoading(false)
      }
    }

    loadSettings()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">HRM Settings</h2>
            </div>
            <button
              onClick={refreshSettings}
              className="bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg border border-gray-300 flex items-center space-x-2 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Settings List */}
        <div className="p-6">
          {settings.length === 0 ? (
            <div className="text-center py-12">
              <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No HRM settings found</p>
              <p className="text-sm text-gray-400">Settings will appear here when available</p>
            </div>
          ) : (
            <div className="space-y-4">
              {settings.map((setting) => (
                <div key={setting._id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white rounded-lg">
                        {getSettingIcon(setting.key)}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">
                          {setting.description}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Key: {setting.key}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={getCurrentValue(setting)}
                          onChange={(e) => handleValueChange(setting.key, e.target.value)}
                          className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          step="0.01"
                          min="0"
                          disabled={!setting.isEditable}
                        />
                        {(setting.key.includes('CONTRIBUTION') || setting.key.includes('TAX')) && (
                          <span className="text-sm text-gray-500">%</span>
                        )}
                        {setting.key.includes('GENERATION_DAY') && (
                          <span className="text-sm text-gray-500">day</span>
                        )}
                      </div>
                      
                      {hasChanges(setting) && (
                        <button
                          onClick={() => saveSetting(setting.key, editedValues[setting.key])}
                          disabled={saving}
                          className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-3 py-2 rounded-lg flex items-center space-x-1 transition-colors text-sm"
                        >
                          {saving ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                          <span>Save</span>
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Setting metadata */}
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
                    <span>Type: {setting.dataType}</span>
                    <span>
                      Updated: {setting.updatedAt ? new Date(setting.updatedAt).toLocaleDateString() : 'Never'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default HRMSettings
